// src/services/audioService.ts

import { db } from '../config/firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import { 
  AudioMetadata, 
  AudioFilters, 
  DatasetOrigin, 
  AudioFocus, 
  AgeGroup,
  BiologicalSex, 
  AudioStatus, 
  SignalQuality,
  MurmurType,
  MurmurTiming,
  MurmurShape,
  MurmurGrading,
  MurmurPitch,
  MurmurQuality,
  Outcome,
  Campaign
} from '../types/audio.types';
import Papa from 'papaparse';

// Configurações do bucket GCP
const BUCKET_NAME = 'she-cardio-data';
const BASE_PATH = 'the-circor-digiscope-phonocardiogram-dataset-1.0.3/training_data';
const METADATA_CSV_PATH = 'the-circor-digiscope-phonocardiogram-dataset-1.0.3/training_data.csv';

// Cache do CSV (evita downloads repetidos)
let csvCache: any[] | null = null;
let labelsCache: Map<string, any> | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Mapeia localização para foco cardíaco
 */
function mapFocus(location: string): AudioFocus {
  const mapping: Record<string, AudioFocus> = {
    'AV': AudioFocus.AORTIC,
    'PV': AudioFocus.PULMONIC,
    'MV': AudioFocus.MITRAL,
    'TV': AudioFocus.TRICUSPID,
    'Phc': AudioFocus.PHC
  };
  return mapping[location] || AudioFocus.UNKNOWN;
}

/**
 * ✅ ADICIONADO: Mapeia faixa etária (Age) para AgeGroup
 */
function mapAgeGroup(age: string | null): AgeGroup {
  if (!age) return AgeGroup.UNKNOWN;
  
  const normalized = age.trim();
  if (normalized === 'Neonate') return AgeGroup.NEONATE;
  if (normalized === 'Infant') return AgeGroup.INFANT;
  if (normalized === 'Child') return AgeGroup.CHILD;
  if (normalized === 'Adolescent') return AgeGroup.ADOLESCENT;
  if (normalized === 'Young Adult') return AgeGroup.YOUNG_ADULT;
  
  return AgeGroup.UNKNOWN;
}

/**
 * Mapeia sexo
 */
function mapSex(sex: string): BiologicalSex {
  if (sex === 'Male') return BiologicalSex.MALE;
  if (sex === 'Female') return BiologicalSex.FEMALE;
  return BiologicalSex.UNKNOWN;
}

/**
 * Mapeia murmur type
 */
function mapMurmurType(murmur: string): MurmurType {
  if (murmur === 'Present') return MurmurType.PRESENT;
  if (murmur === 'Absent') return MurmurType.ABSENT;
  return MurmurType.UNKNOWN;
}

/**
 * Mapeia outcome
 */
function mapOutcome(outcome: string): Outcome {
  if (outcome === 'Normal') return Outcome.NORMAL;
  if (outcome === 'Abnormal') return Outcome.ABNORMAL;
  return Outcome.UNKNOWN;
}

/**
 * ✅ CORRIGIDO: Agora mapeia TODAS as campanhas (CC2014, CC2015)
 */
function mapCampaign(campaign: string): Campaign {
  const normalized = campaign?.trim();
  if (normalized === 'CC2014') return Campaign.CC2014;
  if (normalized === 'CC2015') return Campaign.CC2015;
  return Campaign.UNKNOWN;
}

/**
 * Parse seguro de valores numéricos
 */
function safeParseFloat(value: any): number | null {
  if (!value || value === 'nan' || value === 'NaN') return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Parse seguro de valores booleanos
 */
function safeParseBoolean(value: any): boolean | null {
  if (!value || value === 'nan' || value === 'NaN') return null;
  if (value === 'True' || value === 'true' || value === true) return true;
  if (value === 'False' || value === 'false' || value === false) return false;
  return null;
}

/**
 * Parse seguro de strings (trata nan como null)
 */
function safeParseString(value: any): string | null {
  if (!value || value === 'nan' || value === 'NaN') return null;
  return String(value).trim();
}

/**
 * Lê o CSV do bucket GCP via HTTP direto
 * CORS já configurado via gsutil
 */
async function readCSVFromBucket(): Promise<any[]> {
  // Verificar cache
  const now = Date.now();
  if (csvCache && (now - cacheTimestamp < CACHE_DURATION)) {
    console.log('Usando CSV do cache');
    return csvCache;
  }

  try {
    // URL pública do bucket GCP
    const csvUrl = `https://storage.googleapis.com/${BUCKET_NAME}/${METADATA_CSV_PATH}`;
    
    console.log('Baixando CSV do bucket GCP...');
    const response = await fetch(csvUrl);
    
    if (!response.ok) {
      throw new Error(`Erro ao baixar CSV: ${response.status} ${response.statusText}`);
    }

    const csvText = await response.text();
    
    // Parse do CSV
    const parseResult = await new Promise<any[]>((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data),
        error: (error: any) => reject(error)
      });
    });

    // Atualizar cache
    csvCache = parseResult;
    cacheTimestamp = now;
    
    console.log(`CSV carregado: ${parseResult.length} registros`);
    return parseResult;
    
  } catch (error) {
    console.error('Erro ao ler CSV:', error);
    throw new Error('Falha ao carregar metadados dos áudios');
  }
}

/**
 * Busca todos os labels do Firestore
 */
async function getAllLabels(): Promise<Map<string, any>> {
  // Verificar cache
  const now = Date.now();
  if (labelsCache && (now - cacheTimestamp < CACHE_DURATION)) {
    console.log('Usando labels do cache');
    return labelsCache;
  }

  const labelsMap = new Map<string, any>();

  try {
    const labelsQuery = query(
      collection(db, 'labels')
    );
    
    const snapshot = await getDocs(labelsQuery);

    // Agrupar por audioId, mantendo apenas o mais recente
    const labelsByAudio = new Map<string, any[]>();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const audioId = data.audioId;
      
      if (!labelsByAudio.has(audioId)) {
        labelsByAudio.set(audioId, []);
      }
      
      labelsByAudio.get(audioId)!.push({
        ...data,
        created_at: data.created_at?.toDate()
      });
    });

    // Pegar apenas o label mais recente de cada áudio
    labelsByAudio.forEach((labels, audioId) => {
      const sorted = labels.sort((a, b) => 
        b.created_at.getTime() - a.created_at.getTime()
      );
      
      labelsMap.set(audioId, {
        label: sorted[0].label,
        comment: sorted[0].comment,
        author_name: sorted[0].author_name,
        created_at: sorted[0].created_at
      });
    });

    // Atualizar cache
    labelsCache = labelsMap;
    
    console.log(`Labels carregados: ${labelsMap.size} áudios rotulados`);
    return labelsMap;
    
  } catch (error) {
    console.error('Erro ao buscar labels:', error);
    return new Map();
  }
}

/**
 * Aplica filtros aos áudios
 */
function applyFilters(audios: AudioMetadata[], filters: AudioFilters): AudioMetadata[] {
  let filtered = [...audios];

  // Filtros básicos existentes
  if (filters.focus && filters.focus.length > 0) {
    filtered = filtered.filter(a => filters.focus!.includes(a.focus));
  }

  if (filters.biological_sex && filters.biological_sex.length > 0) {
    filtered = filtered.filter(a => filters.biological_sex!.includes(a.biological_sex));
  }

  if (filters.signal_quality && filters.signal_quality.length > 0) {
    filtered = filtered.filter(a => filters.signal_quality!.includes(a.signal_quality));
  }

  if (filters.subject_id) {
    filtered = filtered.filter(a => a.subject_id === filters.subject_id);
  }

  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter(a => filters.status!.includes(a.status));
  }

  // Novos filtros dos campos do CSV
  if (filters.murmur && filters.murmur.length > 0) {
    filtered = filtered.filter(a => a.murmur && filters.murmur!.includes(a.murmur));
  }

  if (filters.outcome && filters.outcome.length > 0) {
    filtered = filtered.filter(a => a.outcome && filters.outcome!.includes(a.outcome));
  }

  if (filters.campaign && filters.campaign.length > 0) {
    filtered = filtered.filter(a => a.campaign && filters.campaign!.includes(a.campaign));
  }

  // ✅ CORREÇÃO: pregnancy_status agora é boolean | null ao invés de array
  if (filters.pregnancy_status !== undefined && filters.pregnancy_status !== null) {
    filtered = filtered.filter(a => a.pregnancy_status === filters.pregnancy_status);
  }

  // Filtros de murmúrio sistólico
  if (filters.systolic_murmur_timing && filters.systolic_murmur_timing.length > 0) {
    filtered = filtered.filter(a => 
      a.systolic_murmur_timing && filters.systolic_murmur_timing!.includes(a.systolic_murmur_timing)
    );
  }

  if (filters.systolic_murmur_grading && filters.systolic_murmur_grading.length > 0) {
    filtered = filtered.filter(a => 
      a.systolic_murmur_grading && filters.systolic_murmur_grading!.includes(a.systolic_murmur_grading)
    );
  }

  return filtered;
}

/**
 * Busca áudios do bucket com filtros aplicados
 */
export const getAudios = async (
  filters: AudioFilters = {},
  maxResults: number = 10000
): Promise<AudioMetadata[]> => {
  try {
    const csvData = await readCSVFromBucket();
    const labelsMap = await getAllLabels();

    // Usar Map para garantir unicidade de audioId
    const audiosMap = new Map<string, AudioMetadata>();

    for (const row of csvData) {
      const patientId = String(row['Patient ID']);
      const locationsStr = String(row['Recording locations:']).trim();
      
      // Dividir localizações (ex: "AV+PV+TV+MV" → ["AV", "PV", "TV", "MV"])
      const locations = locationsStr.split('+').map(loc => loc.trim());
      
      // Parsear idade
      const ageValue = safeParseString(row['Age']);
      // const ageNumber = safeParseFloat(row['Age']);
      
      // ✅ CORRIGIDO: Duração estimada baseada no sample rate padrão do dataset
      // CirCor usa áudios de ~30s a 60s tipicamente
      // Em produção, pode-se calcular via AudioContext se necessário
      const estimatedDuration = 30.0; // Duração típica dos áudios CirCor
      
      // Criar uma entrada separada para cada localização
      for (const location of locations) {
        const audioId = `${patientId}_${location}`;

        // Se audioId já existe, pular (evita duplicatas)
        if (audiosMap.has(audioId)) {
          continue;
        }

        const audio: AudioMetadata = {
          audioId,
          subject_id: patientId,
          additional_id: safeParseString(row['Additional ID']),
          
          // Metadados técnicos
          gcs_path: `gs://${BUCKET_NAME}/${BASE_PATH}/${audioId}.wav`,
          dataset_origin: DatasetOrigin.CIRCOR_1_0_3,
          sample_rate_hz: 4000,
          duration_s: estimatedDuration, // ✅ CORRIGIDO: agora com duração estimada
          bit_depth: 16,
          channels: 1,
          tsv_path: `gs://${BUCKET_NAME}/${BASE_PATH}/${audioId}.tsv`,
          status: AudioStatus.READY,
          signal_quality: SignalQuality.UNKNOWN,
          ingested_at: new Date(),
          
          // Dados demográficos
          age: ageValue,
          age_group: mapAgeGroup(ageValue), // ✅ ADICIONADO: mapeamento de age_group
          biological_sex: mapSex(row['Sex']),
          height_cm: safeParseFloat(row['Height']),
          weight_kg: safeParseFloat(row['Weight']),
          pregnancy_status: safeParseBoolean(row['Pregnancy status']),
          
          // Localização e foco
          focus: mapFocus(location),
          recording_locations: locationsStr,
          
          // Murmúrio geral
          murmur: mapMurmurType(row['Murmur']),
          murmur_locations: safeParseString(row['Murmur locations']),
          most_audible_location: safeParseString(row['Most audible location']),
          
          // Murmúrio sistólico
          systolic_murmur_timing: safeParseString(row['Systolic murmur timing']) as MurmurTiming | null,
          systolic_murmur_shape: safeParseString(row['Systolic murmur shape']) as MurmurShape | null,
          systolic_murmur_grading: safeParseString(row['Systolic murmur grading']) as MurmurGrading | null,
          systolic_murmur_pitch: safeParseString(row['Systolic murmur pitch']) as MurmurPitch | null,
          systolic_murmur_quality: safeParseString(row['Systolic murmur quality']) as MurmurQuality | null,
          
          // Murmúrio diastólico
          diastolic_murmur_timing: safeParseString(row['Diastolic murmur timing']) as MurmurTiming | null,
          diastolic_murmur_shape: safeParseString(row['Diastolic murmur shape']) as MurmurShape | null,
          diastolic_murmur_grading: safeParseString(row['Diastolic murmur grading']) as MurmurGrading | null,
          diastolic_murmur_pitch: safeParseString(row['Diastolic murmur pitch']) as MurmurPitch | null,
          diastolic_murmur_quality: safeParseString(row['Diastolic murmur quality']) as MurmurQuality | null,
          
          // Resultado e campanha
          outcome: mapOutcome(row['Outcome']),
          campaign: mapCampaign(row['Campaign']), // ✅ CORRIGIDO: agora suporta CC2014 e CC2015
          
          // Label original (para compatibilidade)
          label_original: `Murmur: ${row['Murmur'] || 'Unknown'}; Outcome: ${row['Outcome'] || 'Unknown'}`
        };

        const label = labelsMap.get(audioId);
        if (label) {
          audio.current_label = label;
        }

        audiosMap.set(audioId, audio);
      }
    }

    // Converter Map para Array
    const audios = Array.from(audiosMap.values());
    
    console.log(`Áudios únicos processados: ${audios.length}`);

    let filtered = applyFilters(audios, filters);
    filtered = filtered.slice(0, maxResults);

    return filtered;
    
  } catch (error: any) {
    console.error('Erro ao buscar áudios:', error);
    throw new Error(error.message || 'Falha ao buscar áudios');
  }
};

/**
 * Busca um áudio específico por ID
 */
export const getAudioById = async (audioId: string): Promise<AudioMetadata | null> => {
  try {
    const csvData = await readCSVFromBucket();
    
    // audioId formato: "23625_AV"
    const [patientId, targetLocation] = audioId.split('_');
    
    const row = csvData.find(r => String(r['Patient ID']) === patientId);

    if (!row) {
      return null;
    }

    const ageValue = safeParseString(row['Age']);
    const estimatedDuration = 30.0;

    const audio: AudioMetadata = {
      audioId,
      subject_id: patientId,
      additional_id: safeParseString(row['Additional ID']),
      
      // Metadados técnicos
      gcs_path: `gs://${BUCKET_NAME}/${BASE_PATH}/${audioId}.wav`,
      dataset_origin: DatasetOrigin.CIRCOR_1_0_3,
      sample_rate_hz: 4000,
      duration_s: estimatedDuration, // ✅ CORRIGIDO
      bit_depth: 16,
      channels: 1,
      tsv_path: `gs://${BUCKET_NAME}/${BASE_PATH}/${audioId}.tsv`,
      status: AudioStatus.READY,
      signal_quality: SignalQuality.UNKNOWN,
      ingested_at: new Date(),
      
      // Dados demográficos
      age: ageValue,
      age_group: mapAgeGroup(ageValue), // ✅ ADICIONADO: mapeamento de age_group
      biological_sex: mapSex(row['Sex']),
      height_cm: safeParseFloat(row['Height']),
      weight_kg: safeParseFloat(row['Weight']),
      pregnancy_status: safeParseBoolean(row['Pregnancy status']),
      
      // Localização e foco
      focus: mapFocus(targetLocation),
      recording_locations: String(row['Recording locations:']),
      
      // Murmúrio geral
      murmur: mapMurmurType(row['Murmur']),
      murmur_locations: safeParseString(row['Murmur locations']),
      most_audible_location: safeParseString(row['Most audible location']),
      
      // Murmúrio sistólico
      systolic_murmur_timing: safeParseString(row['Systolic murmur timing']) as MurmurTiming | null,
      systolic_murmur_shape: safeParseString(row['Systolic murmur shape']) as MurmurShape | null,
      systolic_murmur_grading: safeParseString(row['Systolic murmur grading']) as MurmurGrading | null,
      systolic_murmur_pitch: safeParseString(row['Systolic murmur pitch']) as MurmurPitch | null,
      systolic_murmur_quality: safeParseString(row['Systolic murmur quality']) as MurmurQuality | null,
      
      // Murmúrio diastólico
      diastolic_murmur_timing: safeParseString(row['Diastolic murmur timing']) as MurmurTiming | null,
      diastolic_murmur_shape: safeParseString(row['Diastolic murmur shape']) as MurmurShape | null,
      diastolic_murmur_grading: safeParseString(row['Diastolic murmur grading']) as MurmurGrading | null,
      diastolic_murmur_pitch: safeParseString(row['Diastolic murmur pitch']) as MurmurPitch | null,
      diastolic_murmur_quality: safeParseString(row['Diastolic murmur quality']) as MurmurQuality | null,
      
      // Resultado e campanha
      outcome: mapOutcome(row['Outcome']),
      campaign: mapCampaign(row['Campaign']), // ✅ CORRIGIDO
      
      // Label original
      label_original: `Murmur: ${row['Murmur'] || 'Unknown'}; Outcome: ${row['Outcome'] || 'Unknown'}`
    };

    // Buscar label
    const labelsMap = await getAllLabels();
    const label = labelsMap.get(audioId);
    if (label) {
      audio.current_label = label;
    }

    return audio;
    
  } catch (error: any) {
    console.error('Erro ao buscar áudio por ID:', error);
    return null;
  }
};

/**
 * Obtém estatísticas dos áudios
 */
export const getAudioStats = async (): Promise<any> => {
  try {
    const csvData = await readCSVFromBucket();
    const labelsMap = await getAllLabels();

    let totalAudios = 0;
    const stats = {
      total_audios: 0,
      labeled_audios: labelsMap.size,
      unlabeled_audios: 0,
      by_focus: {} as Record<string, number>,
      by_sex: {} as Record<string, number>,
      by_outcome: {} as Record<string, number>,
      by_murmur: {} as Record<string, number>,
      by_campaign: {} as Record<string, number>
    };

    csvData.forEach(row => {
      const locationsStr = String(row['Recording locations:']).trim();
      const locations = locationsStr.split('+').map(loc => loc.trim());
      
      locations.forEach(location => {
        totalAudios++;
        const focus = mapFocus(location);
        stats.by_focus[focus] = (stats.by_focus[focus] || 0) + 1;
      });
      
      const sex = mapSex(row['Sex']);
      const outcome = mapOutcome(row['Outcome']);
      const murmur = mapMurmurType(row['Murmur']);
      const campaign = mapCampaign(row['Campaign']); // ✅ CORRIGIDO
      
      stats.by_sex[sex] = (stats.by_sex[sex] || 0) + 1;
      stats.by_outcome[outcome] = (stats.by_outcome[outcome] || 0) + 1;
      stats.by_murmur[murmur] = (stats.by_murmur[murmur] || 0) + 1;
      stats.by_campaign[campaign] = (stats.by_campaign[campaign] || 0) + 1;
    });

    stats.total_audios = totalAudios;
    stats.unlabeled_audios = totalAudios - labelsMap.size;

    return stats;
    
  } catch (error: any) {
    console.error('Erro ao buscar estatísticas:', error);
    return {
      total_audios: 0,
      labeled_audios: 0,
      unlabeled_audios: 0
    };
  }
};

/**
 * Limpa o cache (útil após salvar labels)
 */
export const clearCache = () => {
  csvCache = null;
  labelsCache = null;
  cacheTimestamp = 0;
};
